// Client-side subscription tier catalog for marketing surfaces (the pricing
// page and the landing-page pricing summary; the account/billing page will
// reuse it later).
//
// MIRROR — display copy only, no entitlement enforcement. The canonical
// numbers live server-side; keep these in sync with:
//   - Free tier:  packages/functions/shared/entitlements.py  (FREE_ENTITLEMENTS)
//   - Paid tiers: packages/functions/billing/api/subscription_plans.json
//
// 4K positioning (locked model): every tier can export up to 4K. The Tweedia
// watermark — not resolution — is the free differentiator. Paid tiers remove it.

import { poolBytesFromGb } from '../photoUpload/caps';

// One-time credits granted when a new account is created (Free onboarding).
export const FREE_SIGNUP_CREDITS = 10;

// Founding-rate protection: a subscription's founding price and at-signup
// allotments stay locked while it is active, with a lapse grace window before
// it reverts to the current public rate. Mirrors the server-side grace window
// applied by the Stripe webhook.
export const FOUNDING_LAPSE_GRACE_DAYS = 60;

export const PRICING_TIERS = [
  {
    id: 'free',
    planId: null,
    name: 'Free',
    founding: false,
    priceLabel: '$0',
    priceSuffix: 'forever',
    tagline: 'Try Tweedia end to end — no credit card.',
    storageGb: 2,
    monthlyCredits: 0,
    signupCredits: FREE_SIGNUP_CREDITS,
    watermarkFree: false,
    retention: '30-day photo retention',
    seats: 1,
    features: [
      '2 GB pooled cloud storage',
      'Unlimited projects',
      '10 starter credits',
      'Export any resolution, up to 4K',
      'Renders carry a Tweedia watermark',
      'Raw photos kept 30 days after a project’s last activity',
    ],
    ctaLabel: 'Start free',
  },
  {
    id: 'solo_founding',
    planId: 'solo_founding',
    name: 'Solo',
    founding: true,
    priceLabel: '$15',
    priceSuffix: '/month',
    tagline: 'For the individual builder documenting one site at a time.',
    storageGb: 50,
    monthlyCredits: 75,
    watermarkFree: true,
    retention: 'Photos kept while subscribed',
    seats: 1,
    featured: true,
    features: [
      '50 GB pooled cloud storage',
      '75 render credits every month',
      'Unlimited projects',
      'Watermark-free exports, up to 4K',
      'Your raw photos stay put while you’re subscribed',
      'Top up credits any time',
    ],
    ctaLabel: 'Choose Solo',
  },
  {
    id: 'pro_founding',
    planId: 'pro_founding',
    name: 'Pro',
    founding: true,
    priceLabel: '$39',
    priceSuffix: '/month',
    tagline: 'For the pro running several long-haul projects at once.',
    storageGb: 250,
    monthlyCredits: 250,
    watermarkFree: true,
    retention: 'Photos kept while subscribed',
    seats: 1,
    features: [
      '250 GB pooled cloud storage',
      '250 render credits every month',
      'Unlimited projects',
      'Watermark-free exports, up to 4K',
      'Your raw photos stay put while you’re subscribed',
      'Top up credits any time',
    ],
    ctaLabel: 'Choose Pro',
  },
];

// Is any self-serve tier's pooled-storage allowance bigger than `poolBytes` —
// the pool the user is actually metered against (caps.poolTotalBytes)? Gates
// "upgrade for more space" upsells so the top tier, and anyone outside the
// self-serve catalog (e.g. Business), isn't pitched an upgrade that doesn't
// exist. Unknown pool → false: withhold the pitch rather than sell a fix that
// can't work.
//
// Takes the effective pool rather than a tier id on purpose: a founding
// member's grandfather floor is a write-once at-signup snapshot, so
// derive_entitlements (packages/functions/shared/entitlements.py) hands back
// storageGb = max(catalog, floor). A Solo whose floor is 500 GB therefore has a
// 500 GB pool while the catalog still says 50 — comparing catalog to catalog
// would pitch them Pro's 250 GB, an "upgrade" that shrinks them.
export function hasLargerStoragePoolThan(poolBytes) {
  if (!Number.isFinite(poolBytes)) return false;
  return PRICING_TIERS.some((t) => poolBytesFromGb(t.storageGb) > poolBytes);
}

// Not a self-serve tier — a "contact us" card. Held back until the higher-volume
// / team features exist, so it makes no self-serve promises.
export const BUSINESS_TIER = {
  id: 'business',
  name: 'Business',
  priceLabel: 'Contact us',
  tagline: 'Teams and higher volume.',
  features: [
    'Everything in Pro',
    'Custom storage & credit volume',
    'Team seats',
    'Priority support',
  ],
  ctaLabel: 'Contact sales',
  contactEmail: 'support@tweediastudio.com',
};
